import { Plane, Instagram, Twitter, Facebook, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

const Footer = () => {
  return (
    <footer className="relative bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] text-white py-16 overflow-hidden">
      {/* Decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-accent/5 pointer-events-none" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute top-20 left-1/4 w-px h-32 bg-gradient-to-b from-primary/20 to-transparent opacity-50" />
      <div className="absolute top-20 right-1/4 w-px h-32 bg-gradient-to-b from-accent/20 to-transparent opacity-50" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4 group">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-md group-hover:bg-primary/30 transition-all duration-300" />
                <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Plane className="w-5 h-5 text-white" />
                </div>
              </div>
              <span className="font-display text-2xl font-semibold group-hover:scale-105 transition-transform duration-300">
                Slate<span className="text-primary">us</span>
              </span>
            </Link>
            <p className="text-white/70 text-sm mb-4 leading-relaxed">
              A Clean Slate for Modern Dating. Connect through shared travel experiences.
            </p>
            <p className="text-white/60 text-xs mb-6 leading-relaxed">
              Safe Travel Guarantee • Verified Profiles • Expert Guides
            </p>
            <div className="flex gap-3">
              <a 
                href="#" 
                className="group relative w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rotate-6"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-white/70 group-hover:text-primary transition-colors duration-300" />
              </a>
              <a 
                href="#" 
                className="group relative w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:-rotate-6"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5 text-white/70 group-hover:text-primary transition-colors duration-300" />
              </a>
              <a 
                href="#" 
                className="group relative w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rotate-6"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-white/70 group-hover:text-primary transition-colors duration-300" />
              </a>
              <a 
                href="#" 
                className="group relative w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:-rotate-6"
                aria-label="Email"
              >
                <Mail className="w-5 h-5 text-white/70 group-hover:text-primary transition-colors duration-300" />
              </a>
            </div>
          </div>

          {/* Company */}
          <div className="relative">
            <div className="absolute -left-6 top-0 w-px h-full bg-gradient-to-b from-primary/20 via-primary/10 to-transparent hidden md:block" />
            <h4 className="font-semibold mb-6 text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              Company
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link 
                  to="/about" 
                  className="text-white/70 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block group"
                >
                  <span className="group-hover:underline">About Us</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/blogs" 
                  className="text-white/70 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block group"
                >
                  <span className="group-hover:underline">Blogs</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div className="relative">
            <div className="absolute -left-6 top-0 w-px h-full bg-gradient-to-b from-accent/20 via-accent/10 to-transparent hidden md:block" />
            <h4 className="font-semibold mb-6 text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-accent" />
              Support
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link 
                  to="/about#contact" 
                  className="text-white/70 hover:text-accent transition-all duration-300 hover:translate-x-1 inline-block group"
                >
                  <span className="group-hover:underline">Contact Us</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy" 
                  className="text-white/70 hover:text-accent transition-all duration-300 hover:translate-x-1 inline-block group"
                >
                  <span className="group-hover:underline">Privacy Policy</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider with decorative element */}
        <div className="relative pt-8 mb-6">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary/50" />
        </div>

        <div className="text-center text-sm text-white/50">
          <p className="hover:text-white/70 transition-colors duration-300">
            © {new Date().getFullYear()} Slate us. All rights reserved. Travel solo, never alone.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
